import { useCallback, useEffect, useState } from 'react';
import type { ApiClient } from '../api/client';
import { useAssistantUI } from '../components/personalAssistant/AssistantUI';
import AccessReview from '../components/personalAssistant/AccessReview';
import ModelPicker from '../components/personalAssistant/ModelPicker';
import ResourceNotifications from '../components/personalAssistant/ResourceNotifications';
import IntegrationConnections from '../components/personalAssistant/IntegrationConnections';
import type { GatewaySnapshot } from '../components/personalAssistant/context';
import './PersonalGatewayPage.css';

/** Setup belongs here; the authenticated shell retains the only chat frame. */
export default function PersonalGatewayPage({ apiClient }: { apiClient: ApiClient }) {
  const { open, openChat } = useAssistantUI();
  const [snapshot, setSnapshot] = useState<GatewaySnapshot | null>(null);
  const [error, setError] = useState(false);
  const refresh = useCallback(async () => {
    try { setSnapshot(await apiClient.get<GatewaySnapshot>('/api/assistant/gateway')); setError(false); }
    catch { setError(true); }
  }, [apiClient]);
  useEffect(() => {
    if (open) return;
    const initial = setTimeout(() => { void refresh(); }, 0);
    const timer = setInterval(() => { void refresh(); }, 10000);
    return () => { clearTimeout(initial); clearInterval(timer); };
  }, [refresh, open]);
  return <div className="personal-assistant-host personal-gateway-page">
    <header className="personal-gateway-intro">
      <span className="page-kicker">Your personal assistant</span>
      <h1>One conversation, wherever you work.</h1>
      <p>Open your assistant on any page. Your conversation and draft stay with you.</p>
      <button onClick={openChat}>Open conversation <span aria-hidden="true">↗</span></button>
    </header>
    <div className="personal-gateway-settings">
      <section className="personal-gateway-card" aria-labelledby="assistant-connection-title">
        <h2 id="assistant-connection-title">Connection</h2>
        <p className="personal-gateway-status" data-state={snapshot?.status}><span aria-hidden="true">{snapshot?.status === 'ready' ? '●' : '○'}</span> {snapshot ? snapshot.configured ? snapshot.status === 'ready' ? 'Your assistant is ready' : 'Your assistant is paused' : 'Connection is being prepared' : 'Checking your connection…'}</p>
        <p>Your assistant opens in a full-screen conversation on your phone. Connection and setup details live here.</p>
        {snapshot?.configured && snapshot.status !== 'ready' && <button onClick={() => { void apiClient.post('/api/assistant/lifecycle', { paused: false }).then(refresh).catch(() => setError(true)); }}>Resume assistant</button>}
        {error && <p role="alert">The connection could not be checked. <button className="ghost" onClick={() => { void refresh(); }}>Check again</button></p>}
      </section>
      <section className="personal-gateway-card" aria-labelledby="assistant-models-title">
        <h2 id="assistant-models-title">Models and subscriptions</h2>
        <p>Connect your subscription once, then switch models whenever you need. A change applies to your next message.</p>
        <ModelPicker apiClient={apiClient} visible={!open} />
      </section>
      <IntegrationConnections state={snapshot?.integrations} apiClient={apiClient} refresh={refresh} />
      <section className="personal-gateway-card personal-gateway-access" aria-labelledby="assistant-access-title">
        <h2 id="assistant-access-title">Access and requests</h2>
        <p>Page contents stay private until you share them. Review resource access and actions here, or use the requests button in your conversation.</p>
        <AccessReview permissions={snapshot?.permissions ?? []} actions={snapshot?.actions ?? []} executionAccess={snapshot?.execution_access ?? []} integrations={snapshot?.integrations} apiClient={apiClient} refresh={refresh} />
      </section>
      <ResourceNotifications status={snapshot?.resource_coordination} ready={snapshot?.status === 'ready'} apiClient={apiClient} refresh={refresh} />
    </div>
  </div>;
}
